import { Link } from 'react-router-dom';

import { t } from '@/i18n';
import { openConsentPrompt } from '@/shared/lib/cookie-consent';
import { Button } from '@/shared/ui/button';
import { StoreButtons } from '@/shared/ui/StoreButtons';

/**
 * TRAD-2241 — 푸터 사업자 등록 정보.
 *
 * 🔴 이건 단순 i18n 대상이 아니다. 전자상거래법 제10조 **법정 표시사항**이고, 상호·대표자·
 * 사업장 소재지·등록번호는 국세청/공정위 사업자정보 조회로 **대조되는 식별자**다. 임의로
 * 번역·로마자화하면 등록 원문과 달라져 대조가 깨진다 — 그래서 로케일과 무관하게 등록된
 * 원문을 그대로 노출한다. davidyoon 결정(TRAD-2234)에서 개인정보 보호책임자 실명을
 * 영문판에도 `윤지원` 한글로 유지한 것과 같은 성격이라 일관되게 간다.
 *
 * 카탈로그가 아니라 상수로 두는 이유: 이 값들은 "번역문이 없는" 게 아니라 **번역 대상이
 * 아닌 등록 사실**이다. en/ko 카탈로그에 같은 값을 두 벌 넣으면 한쪽만 고쳐져 갈라질 수
 * 있는데, 등록 정보가 로케일마다 다른 상황은 존재해선 안 된다.
 *
 * 반대로 **라벨**과 업태·종목(표준산업분류 설명어 — 식별자가 아니라 설명)은 번역한다.
 */
const BUSINESS_REGISTRY = {
  name: '와이컴퍼니',
  representative: '윤지원',
  registrationNo: '604-12-11511',
  address: '서울특별시 동작구 사당로27길 54, 301호(사당동)',
  email: 'xtradingpublic@gmail.com',
} as const;

/**
 * TRAD-3252 — 푸터 링크의 터치 타깃 세로 44 (WCAG 2.5.5 Target Size (Enhanced)).
 *
 * ## 왜 "44×44" 가 아니라 "세로 44" 인가
 * 가로는 **로케일에 따라 변한다** — 같은 링크가 EN "About" 32.4px / KO "서비스 소개" 54.9px 다
 * (2026-09-08 dev 실측). 가로에 고정값을 박으면 한쪽 로케일에서 글자가 잘리거나 빈 공간이 생긴다.
 * 세로는 로케일과 무관하게 16(= line-height)이었으므로 세로만 44 로 올린다.
 *
 * ## 🔴 inline-flex 인데 폭이 안 늘어나는 이유 — 소스로 단정하면 틀리는 자리다
 * 부모가 flex 컨테이너라 자식의 inline-flex 는 **flex 로 blockify** 된다 (TRAD-3237 에서
 * DetailBackButton 이 정확히 이것 때문에 본문 전폭 1280px 을 먹었다). 여기서 안 늘어나는 것은
 * 부모가 items-center 라 align-items:stretch 가 안 걸리고, flex 항목의 기본 flex-basis:auto 가
 * 내용폭을 유지하기 때문이다. **추정이 아니라 브라우저 주입 실측으로 확인했다** — 적용 전후
 * 폭이 83 / 41.5 / 54.9 / 44.5 로 동일했다.
 * 🔴 **부모가 flex 가 아니게 되면 이 전제가 깨진다.** 그때는 w-fit 을 같이 걸어야 한다.
 *
 * ## 한 곳만 고치는 선택지는 없었다
 * 링크들이 flex flex-wrap items-center 한 줄에 있어 **줄 높이가 가장 큰 항목을 따른다.**
 * 하나만 44 로 올려도 줄이 통째로 44 가 되어 푸터가 28px 늘어난다 — 즉 "서비스 소개만" 과
 * "4개 전부" 의 시각 비용이 **완전히 같다** (Δ28.0px, KO/EN × 1280/375 네 조합 실측).
 * 비용이 같은데 3개를 미달로 남길 이유가 없어서 4개를 함께 올렸다.
 */
const FOOTER_LINK_CLASS = 'inline-flex items-center min-h-11 hover:underline hover:text-foreground';

const Footer = () => {
  return (
    <footer data-chrome className="border-t border-border py-4 px-5 text-xs text-muted-foreground">
      {/* TRAD-2998 — 이 문단이 iOS `InvestmentDisclaimerView`/android
          `InvestmentDisclaimerSection`(공용 케이스: crypto/watchGroup/aiReport 등)의 web
          대응물이다. web은 화면마다 별도 박스를 붙이는 대신 이 사이트 전역 footer 한 곳으로
          커버한다(`MainLayout.tsx`가 거의 모든 공개 라우트에 `Footer`를 감싼다) — 축이
          다르거나(B/C/D) 화면 전용 문구가 필요하면(TRAD-2991/2992/2995처럼) 그 화면에
          카탈로그 키를 개별로 추가하는 게 이 레포의 정본 패턴이다.
          🔴 `components/common/InvestmentDisclaimer.tsx`(iOS/android 포팅 시도, 하드코딩
          한글 + 소비처 0건)는 이 footer와 중복이라 삭제했다(TRAD-2998) — 되살리지 말 것,
          "web에도 공용 투자 면책 컴포넌트가 필요하다"는 오독이 이 삭제의 배경이었다. */}
      {/* TRAD-3020 — text-muted-foreground/70 는 라이트 2.81:1·다크 3.39:1 로 WCAG AA
          4.5:1 미달(실측). `/NN` 불투명도가 --muted-foreground(TRAD-2970 에서 이미
          4.5:1 이상으로 튜닝된 값)를 배경과 섞어 대비를 다시 깎는다 — 불투명도 접미사
          제거로 원복(크기는 안 건드림, TRAD-3020 범위 밖). */}
      <p className="text-[11px] leading-relaxed text-muted-foreground mb-3">{t('footer.disclaimer')}</p>
      {/* TRAD-3711 — 푸터 스토어 버튼(종전엔 /about·/support 에만 있어 홈에서 스토어까지 2클릭). /about 과 같은 StoreButtons 를
          작게(size sm) · 푸터 정렬대로 왼쪽. 링크 = 채널 추적 링크 `web_footer`(첫 진입 출처가 있으면 그 이름 — app-links.ts). */}
      <StoreButtons placement="web_footer" size="sm" className="mb-3 justify-start" />
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <Link to="/privacy-policy" className={`font-semibold ${FOOTER_LINK_CLASS}`}>
          {t('footer.link.privacy_policy')}
        </Link>
        <span className="text-border">|</span>
        <Link to="/terms" className={FOOTER_LINK_CLASS}>
          {t('footer.link.terms')}
        </Link>
        <span className="text-border">|</span>
        {/* TRAD-3788 — 커뮤니티 이용규칙(하우스 룰). 종전엔 가입 동의 «보기»로만 닿았다. 앱 정보 메뉴와 같은 /house-rule. */}
        <Link to="/house-rule" className={FOOTER_LINK_CLASS}>
          {t('footer.link.house_rule')}
        </Link>
        <span className="text-border">|</span>
        {/* TRAD-3792 — «쿠키 설정»: 쿠키 동의 배너를 다시 연다(모든 지역). 한국 등 opt-out 지역은 첫 방문에 배너가 없어
            이것이 거부 수단이다. 이동이 아니라 동작이라 Link 가 아니라 공용 Button 이고, 모양은 옆 링크와 같은
            FOOTER_LINK_CLASS 를 그대로 쓴다(룰 1 — 기준 = 위 «이용규칙» 링크). Button 기본 규격(h-9·px·text-sm·
            font-medium·rounded·색)은 여기서 전부 되돌린다. */}
        <Button
          type="button"
          variant="link"
          onClick={openConsentPrompt}
          className={`h-auto p-0 rounded-none text-xs font-normal text-muted-foreground ${FOOTER_LINK_CLASS}`}
        >
          {t('footer.link.cookie_settings')}
        </Button>
        <span className="text-border">|</span>
        <Link to="/about" className={FOOTER_LINK_CLASS}>
          {t('footer.link.about')}
        </Link>
        <span className="text-border">|</span>
        <Link to="/support" className={FOOTER_LINK_CLASS}>
          {t('footer.link.support')}
        </Link>
        <span className="text-border">|</span>
        <span>&copy; {new Date().getFullYear()} XTrading</span>
      </div>
      {/* TRAD-3020 — text-muted-foreground/50 는 라이트 2.01:1·다크 2.27:1 로 WCAG AA
          4.5:1 미달(실측, disclaimer-opacity-contrast-3020.test.ts). 이 블록은 전자상거래법
          §10 법정 표시사항이라 "면책·경고"는 아니지만 같은 결함(불투명도 접미사)이고,
          읽히라고 법이 요구하는 문장이라 함께 고친다(팀장 권고) — 불투명도 접미사 제거로
          원복(크기는 안 건드림). */}
      <div className="mt-3 text-2xs leading-relaxed text-muted-foreground">
        {t('footer.business.name')}: {BUSINESS_REGISTRY.name} &nbsp;|&nbsp; {t('footer.business.representative')}:{' '}
        {BUSINESS_REGISTRY.representative} &nbsp;|&nbsp; {t('footer.business.registration_no')}:{' '}
        {BUSINESS_REGISTRY.registrationNo}
        <br />
        {t('footer.business.address')}: {BUSINESS_REGISTRY.address}
        <br />
        {t('footer.business.type')}: {t('footer.business.type.value')} &nbsp;|&nbsp; {t('footer.business.item')}:{' '}
        {t('footer.business.item.value')}
        <br />
        {t('footer.business.email')}: {BUSINESS_REGISTRY.email}
      </div>
    </footer>
  );
};

export default Footer;
